import { supabase } from '@/integrations/supabase/client';

const isSupabaseOAuthAnalyticsEnabled = import.meta.env.VITE_ENABLE_OAUTH_ANALYTICS === 'true';

interface OAuthMetrics {
  provider: 'google';
  action: 'login_attempt' | 'login_success' | 'login_error' | 'register_attempt' | 'register_success' | 'register_error';
  timestamp: string;
  userAgent?: string;
  error?: string;
  userId?: string;
  sessionId?: string;
  duration?: number; // Time in milliseconds
}

type OAuthAnalyticsInsert = {
  provider: OAuthMetrics['provider'];
  action: OAuthMetrics['action'];
  timestamp: string;
  user_agent?: string;
  error?: string;
  user_id?: string;
  session_id: string;
  duration?: number;
};

class OAuthAnalyticsService {
  private sessionId: string;
  private startTime: number | null = null;
  private supabaseAnalyticsAvailable: boolean;
  private hasLoggedPermissionWarning = false;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.supabaseAnalyticsAvailable = isSupabaseOAuthAnalyticsEnabled;
  }

  private generateSessionId(): string {
    return `oauth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getUserAgent(): string {
    return typeof window !== 'undefined' ? window.navigator.userAgent : '';
  }

  private async trackMetric(metric: Omit<OAuthMetrics, 'timestamp' | 'sessionId' | 'userAgent'>): Promise<void> {
    try {
      const timestamp = new Date().toISOString();
      const userAgent = this.getUserAgent();
      const sessionId = this.sessionId;

      const fullMetric: OAuthMetrics = {
        ...metric,
        timestamp,
        userAgent,
        sessionId,
      };

      // Use snake_case keys expected by the Supabase schema
      const dbPayload: OAuthAnalyticsInsert = {
        provider: metric.provider,
        action: metric.action,
        timestamp,
        user_agent: userAgent,
        error: metric.error,
        user_id: metric.userId,
        session_id: sessionId,
        duration: metric.duration,
      };

      // Store in Supabase analytics table (create this table in production)
      if (this.supabaseAnalyticsAvailable) {
        const { error, status } = await supabase.from('oauth_analytics').insert([dbPayload]);

        if (error) {
          const isPermissionError = status === 403 || error.code === '42501' || error.code === 'PGRST301';

          if (isPermissionError) {
            // Disable Supabase analytics for this session to avoid spamming forbidden requests
            this.supabaseAnalyticsAvailable = false;

            if (!this.hasLoggedPermissionWarning) {
              console.warn(
                'Supabase OAuth analytics disabled for this session (missing permission or policy). Enable VITE_ENABLE_OAUTH_ANALYTICS and confirm RLS/GRANTs before re-enabling.',
                error
              );
              this.hasLoggedPermissionWarning = true;
            }
          } else if (error.code !== 'PGRST116') { // Ignore table not found errors
            console.warn('Failed to track OAuth metric:', error);
          }
        }
      }

      // Also send to external analytics if configured
      this.sendToExternalAnalytics(fullMetric);
    } catch (error) {
      console.warn('Error tracking OAuth metric:', error);
    }
  }

  private sendToExternalAnalytics(metric: OAuthMetrics): void {
    // Send to Google Analytics 4 if gtag is available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      const eventName = `oauth_${metric.action}`;
      (window as any).gtag('event', eventName, {
        provider: metric.provider,
        error: metric.error,
        custom_map: {
          custom_parameter_1: 'provider',
          custom_parameter_2: 'error',
        }
      });
    }

    // Send to PostHog if available
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture(`oauth_${metric.action}`, {
        provider: metric.provider,
        error: metric.error,
        duration: metric.duration,
      });
    }
  }

  startOAuthTracking(provider: 'google', action: 'login_attempt' | 'register_attempt'): void {
    this.startTime = Date.now();
    this.trackMetric({
      provider,
      action,
    });
  }

  trackOAuthSuccess(
    provider: 'google',
    action: 'login_success' | 'register_success',
    userId?: string
  ): void {
    const duration = this.startTime ? Date.now() - this.startTime : undefined;
    this.trackMetric({
      provider,
      action,
      userId,
      duration,
    });
    this.startTime = null;
  }

  trackOAuthError(
    provider: 'google',
    action: 'login_error' | 'register_error',
    error: string,
    userId?: string
  ): void {
    const duration = this.startTime ? Date.now() - this.startTime : undefined;
    this.trackMetric({
      provider,
      action,
      error,
      userId,
      duration,
    });
    this.startTime = null;
  }

  // Method to get OAuth metrics for dashboard
  async getOAuthMetrics(timeRange: '24h' | '7d' | '30d' = '7d'): Promise<any> {
    try {
      const timeAgo = new Date();
      switch (timeRange) {
        case '24h':
          timeAgo.setHours(timeAgo.getHours() - 24);
          break;
        case '7d':
          timeAgo.setDate(timeAgo.getDate() - 7);
          break;
        case '30d':
          timeAgo.setDate(timeAgo.getDate() - 30);
          break;
      }

      const { data, error } = await supabase
        .from('oauth_analytics')
        .select('*')
        .gte('timestamp', timeAgo.toISOString())
        .order('timestamp', { ascending: false });

      if (error) {
        console.warn('Failed to fetch OAuth metrics:', error);
        return this.getMockMetrics(timeRange);
      }

      return this.processMetrics(data);
    } catch (error) {
      console.warn('Error fetching OAuth metrics:', error);
      return this.getMockMetrics(timeRange);
    }
  }

  private processMetrics(data: OAuthMetrics[]): any {
    const total = data.length;
    const successes = data.filter(m => m.action.includes('success')).length;
    const errors = data.filter(m => m.action.includes('error')).length;
    const avgDuration = data
      .filter(m => m.duration)
      .reduce((acc, m) => acc + (m.duration || 0), 0) / data.filter(m => m.duration).length || 0;

    const errorsByType = data
      .filter(m => m.error)
      .reduce((acc, m) => {
        acc[m.error!] = (acc[m.error!] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    return {
      totalAttempts: total,
      successRate: total > 0 ? (successes / total * 100).toFixed(2) : 0,
      errorRate: total > 0 ? (errors / total * 100).toFixed(2) : 0,
      averageCompletionTime: Math.round(avgDuration),
      errorsByType,
      dailyBreakdown: this.getDailyBreakdown(data),
    };
  }

  private getDailyBreakdown(data: OAuthMetrics[]): any[] {
    const breakdown: Record<string, { attempts: number; successes: number; errors: number }> = {};

    data.forEach(metric => {
      const date = new Date(metric.timestamp).toLocaleDateString();
      if (!breakdown[date]) {
        breakdown[date] = { attempts: 0, successes: 0, errors: 0 };
      }
      breakdown[date].attempts++;
      if (metric.action.includes('success')) {
        breakdown[date].successes++;
      }
      if (metric.action.includes('error')) {
        breakdown[date].errors++;
      }
    });

    return Object.entries(breakdown).map(([date, stats]) => ({
      date,
      ...stats,
      successRate: stats.attempts > 0 ? (stats.successes / stats.attempts * 100).toFixed(2) : 0,
    }));
  }

  private getMockMetrics(timeRange: string): any {
    // Return mock data for development or when analytics table doesn't exist
    const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
    const data = [];

    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const attempts = Math.floor(Math.random() * 50) + 10;
      const successes = Math.floor(attempts * (0.85 + Math.random() * 0.1));

      data.push({
        date: date.toLocaleDateString(),
        attempts,
        successes,
        errors: attempts - successes,
        successRate: ((successes / attempts) * 100).toFixed(2),
      });
    }

    const totalAttempts = data.reduce((acc, d) => acc + d.attempts, 0);
    const totalSuccesses = data.reduce((acc, d) => acc + d.successes, 0);
    const totalErrors = data.reduce((acc, d) => acc + d.errors, 0);

    return {
      totalAttempts,
      successRate: ((totalSuccesses / totalAttempts) * 100).toFixed(2),
      errorRate: ((totalErrors / totalAttempts) * 100).toFixed(2),
      averageCompletionTime: Math.floor(Math.random() * 1000) + 2000,
      errorsByType: {
        'redirect_uri_mismatch': 2,
        'access_denied': 5,
        'network_error': 3,
      },
      dailyBreakdown: data.reverse(),
    };
  }
}

// Export singleton instance
export const oauthAnalytics = new OAuthAnalyticsService();

// Export types
export type { OAuthMetrics };
